import { useMutation, useQueryClient } from "@tanstack/react-query";
import { leagueApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The player SELF-WITHDRAW mutation for a league division — DELETE
 * /leagues/registrations/{registration_id} (→ 204) via
 * leagueApi.withdrawRegistration, keyed ONLY by the registration's
 * registration_id (the row's delete key from the My Leagues list). The league
 * analogue of the tournaments useWithdrawEntry: the backend owns the
 * authorization (player self-withdraw) and ALL lifecycle/refund logic — the hook
 * owns NONE of it.
 *
 * On SUCCESS it invalidates the cross-facility "My leagues" arm
 * (queryKeys.me.leagueRegistrations()) so the list REFETCHES the server's truth:
 * the withdrawn row comes back with status="withdrawn" from the server — never an
 * optimistic local splice or status flip. A FAILED withdraw invalidates NOTHING
 * (the row stays "registered"), so a rejected/failed withdraw can never fabricate
 * a withdrawn state.
 *
 * The arg is the registration_id string. The confirm surface (the My Leagues card)
 * owns the single-fire guard + the in-flight disabled state — see MyLeaguesPage.
 *
 * SCAFFOLD: the mutation seam + the success-invalidation are real; the exact
 * confirm copy/behavior on the card are specced by the test-engineer and finalized
 * in GREEN.
 */
export function useWithdrawLeagueRegistration() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (registrationId: string) =>
      leagueApi.withdrawRegistration(registrationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.me.leagueRegistrations(),
      });
    },
  });
}
